import DocumentIconSrc from "assets/images/icons/document-dark.svg";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { patchNotes } from "services/api/taptype/notes";
import { getSinglePatientSharedDocuments } from "services/api/taptype/patients";
import { userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { actions as noteActions, noteSelector } from "store/note";
import { uiSelector } from "store/ui";
import { apiHandler, getNoteSection, isFunction } from "utils/helpers";
import vars from "utils/vars";
import DocumentsSearchInput from "views/components/DocumentsSearchInput";
import LocaleNavLink from "views/components/LocaleNavLink";
import { default as HcpShareDocumentModal } from "views/modals/HcpShareDocument";

const HeaderNoteNavigation = ({ isDemo, isElectronicFormOrder, isReferral }) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { note_id } = useParams();
	const { pathname } = useLocation();

	// ref hooks
	const patchNoteCancelRequestRef = useRef(null);
	const fetchDocumentsCancelRequestRef = useRef(null);

	// redux global state
	const note = useSelector(noteSelector);
	const user = useSelector(userSelector);
	const globals = useSelector(globalsSelector);
	const { isNoteLoading = false, isQuestionsLoading = false } = useSelector(uiSelector);

	// memo hooks
	const section = useMemo(() => getNoteSection(pathname), [pathname]);
	const isSubjectiveSection = section?.toLowerCase() === vars.app.soapSections.subjective || false;
	const isObjectiveSection = section?.toLowerCase() === vars.app.soapSections.objective || false;
	const isUserTypeProfessional = user?.type === vars.app.usersTypes.professional || false;
	const isUserTypeAssociate = user?.type === vars.app.usersTypes.associate || false;
	const isMinimalSOAPLayout = useMemo(
		() => (globals?.segment?.soap_layout || "")?.toLowerCase() === "minimal",
		[globals?.segment?.soap_layout]
	);
	// Memoize documents from note for initialization
	const noteDocuments = useMemo(() => {
		if (note?.documents && Array.isArray(note.documents)) {
			// Check if documents are full objects or just IDs
			const firstDoc = note.documents[0];
			const areFullObjects =
				typeof firstDoc === "object" && firstDoc !== null && "id" in firstDoc && "name" in firstDoc;
			if (areFullObjects) {
				return note.documents;
			}
		}
		return [];
	}, [note?.documents]);

	// Create a stable key for document changes
	const documentsKey = useMemo(() => {
		if (!note?.documents || !Array.isArray(note.documents)) return "";
		// Handle both cases: objects with id property or string IDs
		return note.documents
			.map((doc) => (typeof doc === "string" ? doc : doc?.id))
			.filter(Boolean)
			.sort()
			.join(",");
	}, [note?.documents]);

	// state hooks
	const [deleteDocumentIdLoadingState, setDeleteDocumentIdLoadingState] = useState("");
	const [selectedDocuments, setSelectedDocuments] = useState([]);
	const [refetchDocumentsState, setRefetchDocumentsState] = useState(false);
	const [isDocumentsShownState, setIsDocumentsShownState] = useState(false);

	// formik for DocumentsSearchInput
	const documentsFormik = useFormik({
		initialValues: {
			documents: noteDocuments.length > 0 ? noteDocuments.map(({ id }) => id) : [],
		},
		enableReinitialize: true,
		onSubmit: () => {},
	});

	// event handlers
	const onRemoveSingleDocumentHandler = ({ body }) => {
		if (patchNoteCancelRequestRef.current?.signal) patchNoteCancelRequestRef.current?.abort();
		patchNoteCancelRequestRef.current = new AbortController();

		apiHandler(patchNotes, {
			data: { params: { id: note?.id }, body, options: { signal: patchNoteCancelRequestRef.current.signal } },
			onError: () => setDeleteDocumentIdLoadingState(""),
			onSuccess: ({ data: newNote = {} }) => {
				dispatch(noteActions.update(newNote));
				setDeleteDocumentIdLoadingState("");
				// Update selectedDocuments to match newNote.documents
				if (newNote?.documents) {
					setSelectedDocuments(newNote.documents);
				}
			},
		});
	};

	const handleDocumentsChange = (newDocuments) => {
		setSelectedDocuments(newDocuments);

		if (patchNoteCancelRequestRef.current?.signal) patchNoteCancelRequestRef.current?.abort();
		patchNoteCancelRequestRef.current = new AbortController();

		apiHandler(patchNotes, {
			data: {
				params: { id: note?.id },
				body: {
					documents: newDocuments.map(({ id }) => id),
				},
				options: { signal: patchNoteCancelRequestRef.current.signal },
			},
			onError: () => {
				// Revert to previous state on error
				setSelectedDocuments(note?.documents || []);
			},
			onSuccess: ({ data: newNote = {} }) => {
				dispatch(noteActions.update(newNote));
			},
		});
	};

	const handleDocumentUploadSuccess = (cb, uploadedDocuments) => {
		setRefetchDocumentsState(true);
		const incoming = Array.isArray(uploadedDocuments)
			? uploadedDocuments.filter(Boolean)
			: [uploadedDocuments].filter(Boolean);
		const newDocuments = [...selectedDocuments, ...incoming];
		handleDocumentsChange(newDocuments);
		if (isFunction(cb)) cb();
	};

	// effect hooks
	useEffect(() => {
		return () => {
			if (patchNoteCancelRequestRef.current?.signal) patchNoteCancelRequestRef.current?.abort();
		};
	}, []);

	// Sync selectedDocuments with note.documents whenever note.documents changes
	useEffect(() => {
		if (fetchDocumentsCancelRequestRef.current?.signal) fetchDocumentsCancelRequestRef.current.abort();
		fetchDocumentsCancelRequestRef.current = new AbortController();

		if (note?.id && (note?.patient_id || note?.patient?.id)) {
			if (Array.isArray(note?.documents) && note.documents.length > 0) {
				// Check if documents are full objects or just IDs
				const firstDoc = note.documents[0];
				const areFullObjects =
					typeof firstDoc === "object" && firstDoc !== null && "id" in firstDoc && "name" in firstDoc;

				if (areFullObjects) {
					// Documents are already full objects, use them directly
					const documentIds = noteDocuments.map(({ id }) => id);
					setSelectedDocuments([...noteDocuments]);
					documentsFormik.setFieldValue("documents", documentIds);
				} else {
					// Documents are just IDs, fetch full objects
					const documentIds = note.documents.filter((id) => typeof id === "string" && id);
					if (documentIds.length > 0) {
						apiHandler(getSinglePatientSharedDocuments, {
							data: {
								params: { id: note?.patient_id || note?.patient?.id },
								query: {},
								options: { signal: fetchDocumentsCancelRequestRef.current.signal },
							},
							onError: () => {
								setSelectedDocuments([]);
								documentsFormik.setFieldValue("documents", []);
							},
							onSuccess: ({ data: allDocuments = [] }) => {
								// Filter to only include documents that are in note.documents
								const selectedDocs = allDocuments.filter((doc) => documentIds.includes(doc?.id));
								const selectedIds = selectedDocs.map(({ id }) => id);
								setSelectedDocuments(selectedDocs);
								documentsFormik.setFieldValue("documents", selectedIds);
							},
						});
					} else {
						setSelectedDocuments([]);
						documentsFormik.setFieldValue("documents", []);
					}
				}
			} else {
				// Handle empty or undefined documents - but don't clear if we already have selectedDocuments
				// Only clear if note.documents is explicitly empty array
				if (Array.isArray(note?.documents) && note.documents.length === 0) {
					setSelectedDocuments([]);
					documentsFormik.setFieldValue("documents", []);
				}
			}
		} else if (!note?.id) {
			setSelectedDocuments([]);
			documentsFormik.setFieldValue("documents", []);
		}

		return () => {
			if (fetchDocumentsCancelRequestRef.current?.signal) fetchDocumentsCancelRequestRef.current.abort();
		};
	}, [note?.id, note?.patient_id, note?.patient?.id, documentsKey, noteDocuments]);

	return (
		<div className="root-header-notes-navigation">
			{!isElectronicFormOrder && !isReferral && (
				<div className="root-header-notes-navigation-top pt-4 bg-white border border-primary border-top-0 border-start-0 border-end-0">
					<div className="container">
						<div className="row">
							<div className="col-12">
								<nav aria-label={t("titles.note_navigation")}>
									<ul
										className={`nav nav-tabs nav-fill flex-nowrap border-0 ${
											isMinimalSOAPLayout ? "nav-minimal" : ""
										}`}
										id="root-header-notes-navigation-tabs">
										{[
											vars.app.soapSections.subjective,
											vars.app.soapSections.objective,
											vars.app.soapSections.assessments,
											vars.app.soapSections.plan,
										]?.map((tab, index) => {
											return (
												<li className="nav-item" key={tab}>
													<LocaleNavLink
														to={`${isDemo ? "/demo" : ""}/notes/edit/${note_id}/${tab}`}
														className={`nav-link nav-link-${tab} text-uppercase text-reset rounded-top-3 white-space-nowrap text-start ${
															isNoteLoading ||
															isQuestionsLoading ||
															!note_id ||
															!note?.systems?.length
																? "disabled"
																: ""
														}`}
														tabIndex={
															isNoteLoading ||
															isQuestionsLoading ||
															!note_id ||
															!note?.systems?.length
																? "-1"
																: undefined
														}
														aria-hidden={
															isNoteLoading ||
															isQuestionsLoading ||
															!note_id ||
															!note?.systems?.length
																? "true"
																: undefined
														}
														disabled={
															isNoteLoading ||
															isQuestionsLoading ||
															!note_id ||
															!note?.systems?.length
														}>
														<div className="row gx-2 flex-nowrap align-items-center justify-content-center justify-content-md-start">
															<div className="col-auto">
																<span className="nav-link-counter ratio ratio-1x1 d-block fs-6 rounded-circle">
																	{isNoteLoading ||
																	isQuestionsLoading ||
																	!note_id ||
																	!note?.systems?.length ? (
																		<strong className="d-flex align-items-center justify-content-center placeholder-glow">
																			<span className="placeholder bg-secondary d-block h-95 w-95 rounded-circle">
																				&nbsp;
																			</span>
																		</strong>
																	) : (
																		<strong className="d-flex align-items-center justify-content-center">
																			{index + 1}
																		</strong>
																	)}
																</span>
															</div>
															<div className="col">
																{isNoteLoading ||
																isQuestionsLoading ||
																!note_id ||
																!note?.systems?.length ? (
																	<small className="nav-link-text placeholder-glow d-block">
																		<span className="placeholder bg-secondary w-75">
																			&nbsp;
																		</span>
																	</small>
																) : (
																	<small className="nav-link-text">
																		{!isMinimalSOAPLayout
																			? t(`titles.${tab?.toLowerCase()}`)
																			: section?.toLowerCase()?.trim() ===
																			  tab?.toLowerCase()?.trim()
																			? t(`titles.${tab?.toLowerCase()}`)
																			: t(`titles.${tab?.toLowerCase()}`)?.split(
																					""
																			  )?.[0] || ""}
																	</small>
																)}
															</div>
														</div>
													</LocaleNavLink>
												</li>
											);
										})}
									</ul>
								</nav>
							</div>
						</div>
					</div>
				</div>
			)}
			<div className="">
				<div className="container">
					<div className="row gy-2 gy-lg-0 align-items-center">
						{!isDemo ? (
							<>
								{isNoteLoading || isQuestionsLoading || !note?.id ? (
									<div className="col-auto">
										<span className="bg-primary-200 d-block px-3 py-2 text-capitalize rounded-3">
											<small className="placeholder-glow vstack gap-1" style={{ width: "6rem" }}>
												<span className="placeholder placeholder-sm bg-secondary d-block w-75">
													&nbsp;
												</span>
												<span className="placeholder placeholder-sm bg-secondary d-block w-100">
													&nbsp;
												</span>
											</small>
										</span>
									</div>
								) : (
									<></>
								)}
								{(isReferral || isElectronicFormOrder) &&
									(isNoteLoading || isQuestionsLoading || !note?.id ? (
										<>
											<div className="col-4 col-lg-3 col-xxl-2">
												<div className="rounded border border-primary-200 text-capitalize p-2 hstack gap-2 align-items-center">
													<img
														className="flex-shrink-0 object-fit-scale-down w-16px h-22px"
														src={DocumentIconSrc}
														alt=""
														width="16"
														height="22"
														loading="lazy"
													/>
													<small className="vstack gap-1 lh-1 flex-grow-1 justify-content-center">
														<span className="placeholder placeholder-sm bg-secondary w-75">
															&nbsp;
														</span>
														<strong className="placeholder placeholder-sm bg-secondary w-100">
															&nbsp;
														</strong>
													</small>
												</div>
											</div>
											<div className="col-4 col-lg-3 col-xxl-2">
												<div className="rounded border border-primary-200 text-capitalize p-2 hstack gap-2 align-items-center">
													<img
														className="flex-shrink-0 object-fit-scale-down w-16px h-22px"
														src={DocumentIconSrc}
														alt=""
														width="16"
														height="22"
														loading="lazy"
													/>
													<small className="vstack gap-1 lh-1 flex-grow-1 justify-content-center">
														<span className="placeholder placeholder-sm bg-secondary w-75">
															&nbsp;
														</span>
														<strong className="placeholder placeholder-sm bg-secondary w-100">
															&nbsp;
														</strong>
													</small>
												</div>
											</div>
										</>
									) : (
										<>
											{(note?.patient_id || note?.patient?.id) && (
												<div className="col-12 mb-3">
													<fieldset className="card">
														<div className="row g-gutter align-items-stretch">
															<div className="col-12 col-lg-auto">
																<div className="card-header border-0 px-3 pt-4 pb-3 rounded-0 h-100">
																	<legend className="h5 text-capitalize float-none mb-0">
																		<strong>Linked Documents</strong>
																	</legend>
																</div>
															</div>
															<div className="col-12 col-lg">
																<div className="card-body p-3 d-flex align-items-end rounded-0">
																	<div className="row g-gutter align-items-stretch w-100">
																		<div className="col-12 col-lg">
																			<div className="position-relative">
																				<DocumentsSearchInput
																					form={documentsFormik}
																					patient={{
																						id:
																							note?.patient_id ||
																							note?.patient?.id,
																					}}
																					attachmentsName="documents"
																					allowLabel={false}
																					shouldRefetch={
																						refetchDocumentsState
																					}
																					setShouldRefetch={
																						setRefetchDocumentsState
																					}
																					selectedDocuments={
																						selectedDocuments
																					}
																					setSelectedDocuments={
																						handleDocumentsChange
																					}
																					hideBadges
																				/>
																			</div>
																		</div>
																		<div className="col-12 col-lg-5">
																			<button
																				type="button"
																				onClick={() =>
																					setIsDocumentsShownState(true)
																				}
																				className="btn btn-sm btn-link p-1 border-0 text-decoration-none text-uppercase text-start w-100 lh-1">
																				<span className="d-flex align-items-center justify-content-center gap-2">
																					<span className="btn-icon flex-shrink-0">
																						<svg
																							xmlns="http://www.w3.org/2000/svg"
																							width="27"
																							height="25"
																							viewBox="0 0 22 11"
																							fill="currentColor"
																							className="rotate-n45 w-27px h-25px">
																							<path
																								d="M18.5,16H7A4,4,0,0,1,7,8H19.5a2.5,2.5,0,0,1,0,5H9a1,1,0,0,1,0-2h9.5V9.5H9a2.5,2.5,0,0,0,0,5H19.5a4,4,0,0,0,0-8H7a5.5,5.5,0,0,0,0,11H18.5Z"
																								transform="translate(-1.5 -6.5)"
																							/>
																						</svg>
																					</span>
																					<span className="btn-text flex-grow-1 d-flex flex-column">
																						<strong>
																							Upload new document
																						</strong>
																						<em className="text-secondary">
																							<small>
																								PDF, Maximum size 5MB
																							</small>
																						</em>
																					</span>
																				</span>
																			</button>
																			<HcpShareDocumentModal
																				patientId={
																					note?.patient_id ||
																					note?.patient?.id
																				}
																				onSuccess={handleDocumentUploadSuccess}
																				isShown={isDocumentsShownState}
																				setShown={setIsDocumentsShownState}
																			/>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													</fieldset>
												</div>
											)}
											{((isReferral && note?.internal_referral?.recipient_department?.name_en) ||
												(isElectronicFormOrder && note?.cases?.length > 0) ||
												selectedDocuments?.length > 0) && (
												<>
													{isReferral &&
														note?.internal_referral?.recipient_department?.name_en && (
															<div className="col-auto">
																<span className="bg-primary-200 d-block px-3 py-2 text-capitalize rounded-3">
																	<span className="vstack gap-1 lh-1">
																		<small className="d-block fw-light">
																			Recipient Department
																		</small>
																		<strong className="d-block">
																			{note?.internal_referral
																				?.recipient_department?.name_en || ""}
																		</strong>
																	</span>
																</span>
															</div>
														)}
													{isElectronicFormOrder &&
														note?.cases?.length > 0 &&
														note?.cases?.map((item, index) => (
															<div className="col-auto" key={item?.id || index}>
																<span className="bg-primary-200 d-block px-3 py-2 text-capitalize rounded-3">
																	<span className="vstack gap-1 lh-1">
																		{note?.laboratory_name && (
																			<small className="d-block fw-light">
																				{note.laboratory_name}
																			</small>
																		)}
																		<strong className="d-block">
																			{item?.title}
																		</strong>
																	</span>
																</span>
															</div>
														))}
													{selectedDocuments?.length > 0 &&
														selectedDocuments.map((document) => (
															<div className="col-auto" key={document?.id}>
																<span className="bg-primary-200 d-block px-3 py-2 text-capitalize rounded-3 position-relative">
																	<span className="hstack gap-2 lh-1 align-items-start">
																		<span className="vstack gap-1 lh-1">
																			{document?.file_type && (
																				<small className="d-block fw-light">
																					{vars.documents.types.find(
																						({ value }) =>
																							value ===
																							document?.file_type
																					)?.label || document?.file_type}
																				</small>
																			)}
																			<strong className="d-block">
																				{document?.name || ""}
																			</strong>
																		</span>
																		<button
																			type="button"
																			className="btn btn-link btn-sm p-0 text-dark text-danger-hover border-0 text-decoration-none lh-1"
																			aria-label={t("actions.remove")}
																			onClick={() => {
																				const newDocuments =
																					selectedDocuments.filter(
																						(doc) =>
																							doc?.id !== document?.id
																					);
																				handleDocumentsChange(newDocuments);
																			}}>
																			<svg
																				xmlns="http://www.w3.org/2000/svg"
																				width="1rem"
																				height="1rem"
																				fill="currentColor"
																				viewBox="0 0 16 16">
																				<path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
																			</svg>
																		</button>
																	</span>
																</span>
															</div>
														))}
												</>
											)}
										</>
									))}
								{!isReferral && !isElectronicFormOrder && (
									<>
										<div className="col-12 col-lg-4">
											{(note?.systems?.length > 0 || note?.cases?.length > 0) && (
												<div className="row g-2 align-items-center flex-nowrap">
													{note.systems?.map((item, index) => (
														<div className="col-auto py-3" key={item?.id || index}>
															<div className="ratio ratio-1x1" style={{ width: "52px" }}>
																<div className="d-flex align-items-center justify-content-center bg-white border border-primary rounded-3 p-3 text-truncate">
																	<img
																		src={item?.icon}
																		width="32"
																		height="26"
																		className="object-fit-scale-down img-fluid"
																		alt={item?.title}
																		loading="lazy"
																	/>
																</div>
															</div>
														</div>
													))}
													{note?.cases?.length > 0 && (
														<div className="col">
															<span className="vstack gap-1 lh-sm">
																{note.cases.map((item, index) => (
																	<small
																		className="text-break"
																		key={item?.id || index}>
																		{item?.title || ""}
																	</small>
																))}
															</span>
														</div>
													)}
												</div>
											)}
										</div>
										{(isUserTypeProfessional || isUserTypeAssociate) &&
											(isSubjectiveSection || isObjectiveSection) &&
											Boolean(+globals?.segment?.can_add_new_system) &&
											Boolean(+globals?.segment?.show_all_system_questions) && (
												<div className="col-12 col-md col-lg-4">
													{isNoteLoading || isQuestionsLoading || !note?.id ? (
														<button
															type="button"
															className="btn btn-link btn-sm w-100 px-3 py-2 rounded-3 text-decoration-none text-uppercase mx-auto d-flex align-items-center justify-content-center placeholder-glow"
															disabled>
															<span className="placeholder bg-primary w-75">&nbsp;</span>
														</button>
													) : (
														<button
															type="button"
															className="btn btn-link btn-sm px-3 py-2 rounded-3 text-decoration-none text-uppercase mx-auto d-flex align-items-center justify-content-center"
															data-bs-toggle="modal"
															data-bs-target="#addAnotherQuestionModal">
															{isSubjectiveSection
																? t("actions.add_more_symptoms")
																: t("actions.add_more_examination")}
														</button>
													)}
												</div>
											)}
									</>
								)}
							</>
						) : (
							<div className="col-12">
								<div className="row gx-5 justify-content-center">
									{[
										...(isNoteLoading || isQuestionsLoading || !note?.id
											? Array.apply(null, Array(1)).map((x, i) => i)
											: note?.systems),
									]?.length > 0 && (
										<div className="col-auto">
											<p className="mb-0 hstack gap-3 align-items-center small">
												<strong className="text-uppercase">system: </strong>
												{[
													...(isNoteLoading || isQuestionsLoading || !note?.id
														? Array.apply(null, Array(1)).map((x, i) => i)
														: note?.systems),
												]?.map((item, index) => (
													<span
														className="bg-primary-200 px-3 py-2 text-capitalize rounded-3"
														key={item?.id || index}>
														{isNoteLoading || isQuestionsLoading || !note?.id ? (
															<span
																className="d-block placeholder-glow d-flex align-items-center justify-content-center"
																style={{ width: "4rem" }}>
																<span className="placeholder bg-secondary w-90 rounded">
																	&nbsp;
																</span>
															</span>
														) : (
															item?.title
														)}
													</span>
												))}
											</p>
										</div>
									)}
									{[
										...(isNoteLoading || isQuestionsLoading || !note?.id
											? Array.apply(null, Array(1)).map((x, i) => i)
											: note?.cases || []),
									]?.length > 0 && (
										<div className="col-auto">
											<p className="mb-0 hstack gap-3 align-items-center small">
												<strong className="text-uppercase">medical cases: </strong>
												{[
													...(isNoteLoading || isQuestionsLoading || !note?.id
														? Array.apply(null, Array(1)).map((x, i) => i)
														: note?.cases),
												]?.map((item, index) => (
													<span
														className="bg-primary-200 px-3 py-2 text-capitalize rounded-3"
														key={item?.id || index}>
														{isNoteLoading || isQuestionsLoading || !note?.id ? (
															<span
																className="d-block placeholder-glow d-flex align-items-center justify-content-center"
																style={{ width: "4rem" }}>
																<span className="placeholder bg-secondary w-90 rounded">
																	&nbsp;
																</span>
															</span>
														) : (
															item?.title
														)}
													</span>
												))}
											</p>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default HeaderNoteNavigation;
